import {STATUS} from '@jobseeker/common-core/constants';
import {enqueueSnackbar} from 'notistack';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';
import {
  DreamCompanyRecord,
  GetDreamCompaniesResponse,
  ResponseProfileV2,
  SaveDreamCompaniesParams,
  SaveDreamCompaniesResponse,
} from './types';

const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_USER_SERVICE}/profile/`
);
const profileBaseUrl = `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_USER_SERVICE}/profile/`;

const getCurrentRef = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('ref') ?? undefined;
};

const getProfile = async (sections: {
  pd?: boolean;
  we?: boolean;
  ed?: boolean;
  co?: boolean;
  ai?: boolean;
  atr?: boolean;
  rd?: boolean;
  ap?: boolean;
}): Promise<ResponseProfileV2> => {
  try {
    let fields = '';
    fields += sections.pd ? '1' : '0';
    fields += sections.we ? '1' : '0';
    fields += sections.ed ? '1' : '0';
    fields += sections.co ? '1' : '0';
    fields += sections.ai ? '1' : '0';
    fields += sections.atr ? '1' : '0';
    fields += sections.rd ? '1' : '0';
    // AI projects are the 8th position; older masks stay 7 characters long.
    if (sections.ap) fields += '1';

    const response: any = await axios.get(`v2?fields=${fields}`, {
      withCredentials: true,
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    if (response[STATUS] === 200) {
      return response?.data?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const updateProfile = async (
  profileUpdationParams: ResponseProfileV2,
  version = '',
  updatedBy?: number,
  updationMeta?: string,
  valueUpdated?: 0 | 1
): Promise<ResponseProfileV2> => {
  try {
    const queryParams = new URLSearchParams();
    if (updatedBy !== undefined)
      queryParams.set('updatedBy', String(updatedBy));
    if (updationMeta !== undefined)
      queryParams.set('updationMeta', updationMeta);
    if (valueUpdated !== undefined)
      queryParams.set('valueUpdated', String(valueUpdated));
    const queryString = queryParams.toString();
    const url = queryString ? `v2?${queryString}` : 'v2';

    const response: any = await axios.post(url, profileUpdationParams, {
      withCredentials: true,
      headers: {
        version,
      },
    });
    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

const skipProfesssion = async (): Promise<ResponseProfileV2> => {
  try {
    const response: any = await axios.post(
      'skipwork',
      {},
      {
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response?.data;
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error.response.data.error.message,
        code: error.response.data.error.statusCode,
      },
    };
  }
};

/**
 * POST /profile/ with section `media` — the route that writes the `resume_path`
 * profileLog row.
 *
 * `valueUpdated: 0` marks a confirmation ("my resume is already up to date"): the
 * server logs the affirmation and deliberately does not re-save the path, so
 * `resumeUpdatedAt` is not bumped to today for an upload that never happened.
 */
const patchProfileMedia = async (
  payload: {
    resumePath?: string;
    audioPath?: string;
    videoPath?: string;
    profilePicPath?: string;
    resumeType?: number;
  },
  valueUpdated?: 0 | 1,
  /**
   * Attribution for the profileLog row. Omitted, the server records `String(userId)`,
   * which reads as an ordinary self-serve edit — see `updateProfileLog`.
   */
  updationMeta?: string,
  updatedBy?: number
): Promise<{message?: string; error?: {message?: string; code?: number}}> => {
  try {
    const queryParams = new URLSearchParams();
    if (valueUpdated !== undefined)
      queryParams.set('valueUpdated', String(valueUpdated));
    if (updationMeta !== undefined)
      queryParams.set('updationMeta', updationMeta);
    if (updatedBy !== undefined)
      queryParams.set('updatedBy', String(updatedBy));
    const query = queryParams.toString();
    const queryString = query ? `?${query}` : '';
    const response = await fetch(`${profileBaseUrl}${queryString}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'patch',
        section: 'media',
        payload,
        metadata: {
          ref: getCurrentRef(),
        },
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.status === 200) {
      return data ?? {};
    }
    return {
      error: {
        message:
          data?.error?.message ?? data?.message ?? 'Could not confirm media',
        code: data?.error?.statusCode ?? data?.statusCode ?? response.status,
      },
    };
  } catch (error: any) {
    return {
      error: {
        message: error?.response?.data?.error?.message ?? 'Unknown error',
        code: error?.response?.data?.error?.statusCode ?? 500,
      },
    };
  }
};

/**
 * The dream-companies list has been seen wrapped a couple of different ways
 * (`data`, `dreamCompanies`, or a bare array), so unwrap defensively and let
 * the caller work with a plain array either way.
 */
const unwrapDreamCompanies = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.dreamCompanies)) {
    return payload.dreamCompanies;
  }
  if (Array.isArray(payload?.data?.dreamCompanies)) {
    return payload.data.dreamCompanies;
  }
  return [];
};

const toDreamCompanyRecord = (item: any): DreamCompanyRecord | null => {
  const companyName = item?.companyName ?? item?.company_name ?? item?.name;
  if (!companyName) {
    return null;
  }
  const status = item?.status;
  const centralizedCompanyId =
    item?.centralizedCompanyId ?? item?.centralized_company_id;
  return {
    id: item?.id,
    companyName: String(companyName),
    careerUrl: String(item?.careerUrl ?? item?.career_url ?? item?.url ?? ''),
    centralizedCompanyId:
      centralizedCompanyId == null ? undefined : String(centralizedCompanyId),
    // Deselecting flips `status` to false rather than dropping the row, so only
    // an explicit false means "not on the list"; a missing flag means selected.
    // Strings are checked by value because `Boolean('false')` is true.
    selected:
      typeof status === 'string'
        ? !['false', '0', 'inactive'].includes(status.trim().toLowerCase())
        : status == null || Boolean(status),
  };
};

/** Dream companies already saved against the logged-in seeker's profile. */
const getDreamCompanies = async (): Promise<GetDreamCompaniesResponse> => {
  try {
    const response: any = await axios.get('dream-companies', {
      withCredentials: true,
      headers: {'Cache-Control': 'no-cache'},
    });
    if (response[STATUS] === 200) {
      return {
        data: unwrapDreamCompanies(response?.data)
          .map(toDreamCompanyRecord)
          .filter((record): record is DreamCompanyRecord => Boolean(record)),
      };
    }
    return {data: []};
  } catch (error: any) {
    return {
      error: {
        message: error?.response?.data?.error?.message,
        code: error?.response?.data?.error?.statusCode,
      },
    };
  }
};

/**
 * Saves the seeker's dream companies in a single call so the backend can start
 * watching their careers pages. The endpoint upserts and reports per-company
 * outcomes: entries it accepted come back under `saved` (each tagged `created`
 * or `updated`) and the ones it rejected under `failed`, so a partial failure
 * still resolves successfully rather than throwing.
 */
const saveDreamCompanies = async (
  params: SaveDreamCompaniesParams
): Promise<SaveDreamCompaniesResponse> => {
  try {
    const response: any = await axios.post('dream-companies', params, {
      withCredentials: true,
    });
    if (response[STATUS] === 200) {
      const payload = response?.data?.data ?? response?.data;
      return {
        data: {
          saved: Array.isArray(payload?.saved) ? payload.saved : [],
          failed: Array.isArray(payload?.failed) ? payload.failed : [],
        },
      };
    }
    return {};
  } catch (error: any) {
    return {
      error: {
        message: error?.response?.data?.error?.message,
        code: error?.response?.data?.error?.statusCode,
      },
    };
  }
};

const profileService = {
  getProfile,
  updateProfile,
  patchProfileMedia,
  skipProfesssion,
  getDreamCompanies,
  saveDreamCompanies,
};

export default profileService;
